import { storeToRefs } from 'pinia'
import type { MaybeRefOrGetter } from 'vue'
import { computed, toRef } from 'vue'

import { useDialog } from '@last/core-ui/paprika'

import { t } from '@/i18n'
import { managerConfirmation } from '@/plugins/managerConfirmation'
import { useAuthStore } from '@/store/auth'
import { useTabsStore } from '@/store/tabs'
import type { TabProduct } from '@/types'

export default function useProduct(mixedProduct: MaybeRefOrGetter<TabProduct>) {
  const tabsStore = useTabsStore()
  const authStore = useAuthStore()

  const dialog = useDialog()

  const { updateProductQuantity, removeProduct, getSentToKitchenProducts } = tabsStore
  const { tabs, products } = storeToRefs(tabsStore)
  const { askPinForPrivilege } = authStore

  const product = toRef(mixedProduct)

  const storeProduct = computed(() => products.value[product.value.id])

  const hasBeenSentToKitchen = computed(() => {
    return !!getSentToKitchenProducts(product.value.tab).find(p => p.productId === product.value.id)
  })

  const requiredPrivilege = computed(() => {
    if (tabs.value[product.value.tab].billingStartedTime) {
      return {
        privilege: 'BILLED_PRODUCT_REMOVAL',
        message: t('delete-product.billed-message')
      }
    } else if (hasBeenSentToKitchen.value) {
      return {
        privilege: 'KITCHEN_PRODUCT_REMOVAL',
        message: t('delete-product.ordered-message')
      }
    }
    return false
  })

  const description = computed(() => {
    return [...(product.value.comboProducts || product.value.modifiers || [])]
      .sort((a, b) => a.position - b.position)
      .map(modifier => {
        const quantity = modifier.quantity > 1 ? ` x ${modifier.quantity}` : ''
        return modifier.name + quantity
      })
      .join(', ')
  })

  const productIsPaid = computed(() => {
    const productsPendingToBePayed =
      product.value.discountType === '2x1'
        ? product.value.notPaidQuantity - product.value.discountAmount!
        : product.value.notPaidQuantity
    const isA2x1Product =
      product.value.discountType === '2x1' &&
      product.value.quantity === product.value.discountAmount
    return productsPendingToBePayed === 0 && !isA2x1Product
  })

  const productIsBilled = computed(() => {
    const tab = tabs.value[storeProduct.value.tab]
    const isDelivery = tab.pickupType && ['delivery', 'ownDelivery'].includes(tab.pickupType)
    const productsPendingToBeBilled =
      product.value.discountType === '2x1'
        ? product.value.notBilledQuantity - product.value.discountAmount!
        : product.value.notBilledQuantity
    const isA2x1Product =
      product.value.discountType === '2x1' &&
      product.value.quantity === product.value.discountAmount
    return productsPendingToBeBilled === 0 && !isA2x1Product && !isDelivery
  })

  function updateQuantity(quantity: number) {
    if (quantity < product.value.quantity) {
      deleteProductWithConfirmation(true)
    } else {
      updateProductQuantity({
        productId: product.value.id,
        quantity
      })
    }
  }

  function deleteProduct(deleteSingleUnit = false) {
    if (product.value.quantity > 1 && deleteSingleUnit) {
      updateProductQuantity({
        productId: product.value.id,
        quantity: product.value.quantity - 1
      })
    } else {
      removeProduct(product.value.id)
    }
  }

  function deleteProductWithConfirmation(deleteSingleUnit?: boolean) {
    const privilege = requiredPrivilege.value
    if (privilege && askPinForPrivilege(privilege.privilege)) {
      dialog({
        icon: 'delete',
        title: t('delete-product.title'),
        content: requiredPrivilege.value.message,
        mainLabel: t('delete-product.enter-pin'),
        onConfirm: async () => {
          const hasPermission = await managerConfirmation(privilege.privilege)
          if (hasPermission) {
            deleteProduct(deleteSingleUnit)
          }
        }
      })
    } else {
      deleteProduct(deleteSingleUnit)
    }
  }

  return {
    description,
    productIsPaid,
    productIsBilled,
    deleteProductWithConfirmation,
    updateQuantity
  }
}
